/**
 * Blog Listing Page
 *
 * Handles the blog index page with article listing, category filtering,
 * and pagination. Loads articles from the generated manifest.
 */

import type { ArticleSummary, BlogManifest, CategorySlug, BlogFilterState, PaginationState } from '../src/blog/types';
import { ARTICLES_PER_PAGE, DEFAULT_CATEGORIES } from '../src/blog/types';
import { initWebVitals } from '../src/utils/webVitals';
import { analytics, functions } from '../src/firebase/config';
import { httpsCallable } from 'firebase/functions';

// Initialize Web Vitals tracking
initWebVitals(analytics);

// ============================================================================
// State
// ============================================================================

let manifest: BlogManifest | null = null;
let filterState: BlogFilterState = {
  category: 'all',
  page: 1
};

// ============================================================================
// DOM Elements
// ============================================================================

const categoryNav = document.getElementById('category-nav');
const featuredSection = document.getElementById('featured-article');
const articlesGrid = document.getElementById('articles-grid');
const paginationContainer = document.getElementById('pagination');
const newsletterForm = document.getElementById('blog-newsletter-form');

// ============================================================================
// Initialization
// ============================================================================

async function init(): Promise<void> {
  // Parse URL for category filter
  const path = window.location.pathname;
  const categoryMatch = path.match(/\/blog\/category\/([a-z0-9-]+)/);
  if (categoryMatch) {
    filterState.category = categoryMatch[1] as CategorySlug;
  }

  // Load manifest
  await loadManifest();

  // Set up event listeners
  setupEventListeners();

  // Render the page
  renderPage();
}

async function loadManifest(): Promise<void> {
  try {
    // In development, we'll create a mock manifest
    // In production, this would be loaded from /blog/manifest.json
    const response = await fetch('/blog/manifest.json');
    if (response.ok) {
      manifest = await response.json();
    } else {
      // Use mock data for development
      manifest = createMockManifest();
    }
  } catch {
    // Use mock data if fetch fails
    manifest = createMockManifest();
  }
}

function createMockManifest(): BlogManifest {
  return {
    generatedAt: new Date().toISOString(),
    articles: [
      {
        title: 'How to Create a Virtual Tour in 2025: Complete Beginner\'s Guide',
        slug: 'how-to-create-virtual-tour-2025',
        description: 'Learn everything you need to know about creating stunning virtual tours. From equipment selection to publishing, this comprehensive guide covers it all.',
        author: 'Sonny Cirasuolo',
        publishedAt: '2025-01-15T10:00:00Z',
        category: 'tutorials',
        tags: ['virtual-tour', 'beginner', 'guide'],
        coverImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
        readingTime: 12,
        featured: true
      },
      {
        title: '360 Photography Equipment Guide: Best Cameras Under $500',
        slug: '360-photography-equipment-guide',
        description: 'Discover the best 360 cameras for virtual tours without breaking the bank. We compare top options for quality, ease of use, and value.',
        author: 'Sonny Cirasuolo',
        publishedAt: '2025-01-08T10:00:00Z',
        category: '360-photography',
        tags: ['equipment', 'cameras', 'budget'],
        coverImage: 'https://images.unsplash.com/photo-1502982720700-bfff97f2ecac?w=800',
        readingTime: 8
      },
      {
        title: 'Virtual Museum Tours: How Cultural Institutions Reach Global Audiences',
        slug: 'virtual-museum-tours-global-audiences',
        description: 'Explore how museums and cultural institutions are using virtual tours to reach millions of visitors worldwide.',
        author: 'Sonny Cirasuolo',
        publishedAt: '2025-01-01T10:00:00Z',
        category: 'industry',
        tags: ['museums', 'case-study', 'cultural'],
        coverImage: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=800',
        readingTime: 10
      }
    ],
    categories: DEFAULT_CATEGORIES,
    totalArticles: 3
  };
}

// ============================================================================
// Event Listeners
// ============================================================================

function setupEventListeners(): void {
  // Category tabs
  categoryNav?.querySelectorAll('.category-tab').forEach(tab => {
    tab.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const category = target.dataset.category as CategorySlug | 'all';
      handleCategoryChange(category);
    });
  });

  // Newsletter form
  newsletterForm?.addEventListener('submit', handleNewsletterSubmit);
}

function handleCategoryChange(category: CategorySlug | 'all'): void {
  filterState.category = category;
  filterState.page = 1;

  // Update URL
  if (category === 'all') {
    history.pushState(null, '', '/blog');
  } else {
    history.pushState(null, '', `/blog/category/${category}`);
  }

  // Update active tab
  categoryNav?.querySelectorAll('.category-tab').forEach(tab => {
    const tabCategory = (tab as HTMLElement).dataset.category;
    tab.classList.toggle('active', tabCategory === category);
  });

  // Re-render articles
  renderArticles();
  renderPagination();
}

function handlePageChange(page: number): void {
  filterState.page = page;
  renderArticles();
  renderPagination();

  // Scroll to top of articles
  articlesGrid?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function handleNewsletterSubmit(e: Event): Promise<void> {
  e.preventDefault();

  const form = e.target as HTMLFormElement;
  const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;
  const statusEl = document.getElementById('newsletter-status');
  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  if (!emailInput || !statusEl) return;

  const email = emailInput.value.trim();
  if (!email) return;

  // Disable form
  emailInput.disabled = true;
  submitBtn.disabled = true;
  submitBtn.textContent = 'Subscribing...';

  try {
    const subscribeNewsletter = httpsCallable(functions, 'subscribeNewsletter');
    const result = await subscribeNewsletter({
      email,
      source: 'blog_sidebar',
      sourceUrl: window.location.href
    });

    const data = result.data as { success: boolean; message: string };
    statusEl.textContent = data.message || 'Thanks for subscribing! Check your email to confirm.';
    statusEl.className = 'newsletter-status success';
    emailInput.value = '';
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
    statusEl.textContent = errorMessage;
    statusEl.className = 'newsletter-status error';
  } finally {
    emailInput.disabled = false;
    submitBtn.disabled = false;
    submitBtn.textContent = 'Subscribe';
  }
}

// ============================================================================
// Rendering (with DOMPurify sanitization)
// ============================================================================

function renderPage(): void {
  renderCategoryTabs();
  renderFeaturedArticle();
  renderArticles();
  renderPagination();
}

function renderCategoryTabs(): void {
  // Update active state based on current filter
  categoryNav?.querySelectorAll('.category-tab').forEach(tab => {
    const tabCategory = (tab as HTMLElement).dataset.category;
    tab.classList.toggle('active', tabCategory === filterState.category);
  });
}

function renderFeaturedArticle(): void {
  if (!featuredSection || !manifest) return;

  // Only show featured on first page with 'all' category
  if (filterState.category !== 'all' || filterState.page !== 1) {
    // Clear by replacing with empty content
    while (featuredSection.firstChild) {
      featuredSection.removeChild(featuredSection.firstChild);
    }
    return;
  }

  const featured = manifest.articles.find(a => a.featured);
  if (!featured) {
    while (featuredSection.firstChild) {
      featuredSection.removeChild(featuredSection.firstChild);
    }
    return;
  }

  const category = manifest.categories.find(c => c.slug === featured.category);

  // Build DOM safely
  const article = document.createElement('article');
  article.className = 'featured-article';

  const img = document.createElement('img');
  img.src = featured.coverImage || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800';
  img.alt = featured.title;
  img.className = 'featured-image';

  const content = document.createElement('div');
  content.className = 'featured-content';

  const badge = document.createElement('span');
  badge.className = 'featured-badge';
  badge.textContent = 'Featured';

  const title = document.createElement('h2');
  title.className = 'featured-title';
  const titleLink = document.createElement('a');
  titleLink.href = `/blog/${featured.slug}`;
  titleLink.textContent = featured.title;
  title.appendChild(titleLink);

  const excerpt = document.createElement('p');
  excerpt.className = 'featured-excerpt';
  excerpt.textContent = featured.description;

  const meta = document.createElement('div');
  meta.className = 'featured-meta';

  const categorySpan = document.createElement('span');
  categorySpan.className = 'article-card-category';
  categorySpan.dataset.category = featured.category;
  categorySpan.textContent = category?.name || featured.category;

  const dateSpan = document.createElement('span');
  dateSpan.textContent = formatDate(featured.publishedAt);

  const readTimeSpan = document.createElement('span');
  readTimeSpan.textContent = `${featured.readingTime || 5} min read`;

  meta.appendChild(categorySpan);
  meta.appendChild(dateSpan);
  meta.appendChild(readTimeSpan);

  content.appendChild(badge);
  content.appendChild(title);
  content.appendChild(excerpt);
  content.appendChild(meta);

  article.appendChild(img);
  article.appendChild(content);

  // Clear and append
  while (featuredSection.firstChild) {
    featuredSection.removeChild(featuredSection.firstChild);
  }
  featuredSection.appendChild(article);
}

function renderArticles(): void {
  if (!articlesGrid || !manifest) return;

  const filteredArticles = getFilteredArticles();
  const paginatedArticles = getPaginatedArticles(filteredArticles);

  // Clear existing content
  while (articlesGrid.firstChild) {
    articlesGrid.removeChild(articlesGrid.firstChild);
  }

  if (paginatedArticles.length === 0) {
    const emptyState = document.createElement('div');
    emptyState.className = 'empty-state';
    emptyState.style.gridColumn = '1 / -1';

    const h3 = document.createElement('h3');
    h3.textContent = 'No articles found';

    const p = document.createElement('p');
    p.textContent = 'Check back soon for new content!';

    emptyState.appendChild(h3);
    emptyState.appendChild(p);
    articlesGrid.appendChild(emptyState);
    return;
  }

  paginatedArticles.forEach(article => {
    articlesGrid.appendChild(createArticleCard(article));
  });
}

function createArticleCard(article: ArticleSummary): HTMLElement {
  const category = manifest?.categories.find(c => c.slug === article.category);

  const card = document.createElement('article');
  card.className = 'article-card';

  const imgLink = document.createElement('a');
  imgLink.href = `/blog/${article.slug}`;

  const img = document.createElement('img');
  img.src = article.coverImage || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800';
  img.alt = article.title;
  img.className = 'article-card-image';
  img.loading = 'lazy';
  imgLink.appendChild(img);

  const content = document.createElement('div');
  content.className = 'article-card-content';

  const categorySpan = document.createElement('span');
  categorySpan.className = 'article-card-category';
  categorySpan.dataset.category = article.category;
  categorySpan.textContent = category?.name || article.category;

  const title = document.createElement('h3');
  title.className = 'article-card-title';
  const titleLink = document.createElement('a');
  titleLink.href = `/blog/${article.slug}`;
  titleLink.textContent = article.title;
  title.appendChild(titleLink);

  const excerpt = document.createElement('p');
  excerpt.className = 'article-card-excerpt';
  excerpt.textContent = article.description;

  const meta = document.createElement('div');
  meta.className = 'article-card-meta';

  const dateSpan = document.createElement('span');
  dateSpan.textContent = formatDate(article.publishedAt);

  const readTimeSpan = document.createElement('span');
  readTimeSpan.textContent = `${article.readingTime || 5} min read`;

  meta.appendChild(dateSpan);
  meta.appendChild(readTimeSpan);

  content.appendChild(categorySpan);
  content.appendChild(title);
  content.appendChild(excerpt);
  content.appendChild(meta);

  card.appendChild(imgLink);
  card.appendChild(content);

  return card;
}

function renderPagination(): void {
  if (!paginationContainer || !manifest) return;

  const filteredArticles = getFilteredArticles();
  const pagination = getPaginationState(filteredArticles);

  // Clear existing content
  while (paginationContainer.firstChild) {
    paginationContainer.removeChild(paginationContainer.firstChild);
  }

  if (pagination.totalPages <= 1) {
    return;
  }

  // Previous button
  const prevBtn = document.createElement('button');
  prevBtn.className = 'pagination-btn';
  prevBtn.textContent = '\u2190';
  prevBtn.disabled = pagination.currentPage === 1;
  prevBtn.addEventListener('click', () => handlePageChange(pagination.currentPage - 1));
  paginationContainer.appendChild(prevBtn);

  // Page numbers
  for (let i = 1; i <= pagination.totalPages; i++) {
    const pageBtn = document.createElement('button');
    pageBtn.className = `pagination-btn ${i === pagination.currentPage ? 'active' : ''}`;
    pageBtn.textContent = String(i);
    pageBtn.addEventListener('click', () => handlePageChange(i));
    paginationContainer.appendChild(pageBtn);
  }

  // Next button
  const nextBtn = document.createElement('button');
  nextBtn.className = 'pagination-btn';
  nextBtn.textContent = '\u2192';
  nextBtn.disabled = pagination.currentPage === pagination.totalPages;
  nextBtn.addEventListener('click', () => handlePageChange(pagination.currentPage + 1));
  paginationContainer.appendChild(nextBtn);
}

// ============================================================================
// Helpers
// ============================================================================

function getFilteredArticles(): ArticleSummary[] {
  if (!manifest) return [];

  let articles = manifest.articles;

  // Filter by category
  if (filterState.category !== 'all') {
    articles = articles.filter(a => a.category === filterState.category);
  }

  // Exclude featured article from grid on first page
  if (filterState.category === 'all' && filterState.page === 1) {
    articles = articles.filter(a => !a.featured);
  }

  return articles;
}

function getPaginatedArticles(articles: ArticleSummary[]): ArticleSummary[] {
  const start = (filterState.page - 1) * ARTICLES_PER_PAGE;
  const end = start + ARTICLES_PER_PAGE;
  return articles.slice(start, end);
}

function getPaginationState(articles: ArticleSummary[]): PaginationState {
  return {
    currentPage: filterState.page,
    totalPages: Math.ceil(articles.length / ARTICLES_PER_PAGE),
    articlesPerPage: ARTICLES_PER_PAGE,
    totalArticles: articles.length
  };
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

// ============================================================================
// Initialize
// ============================================================================

init();
